import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Refresh,
  Event as EventIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { PourSummary } from '../../services/pours';
import { formatDateTimeWithZone, formatTimeWithZone } from '../../utils/datetime';

/** The pour summary with the fields this header reads that older
 *  responses may not carry. */
type HeaderPour = PourSummary & {
  /** In Progress but not started yet (backend dashboard_service). */
  scheduled?: boolean | null;
  /** The earliest planned start, naive UTC. */
  starts_at?: string | null;
};

interface PourHeaderProps {
  pour: HeaderPour;
  weather?: any;
  onRefresh?: () => void;
  lastRefresh?: Date;
  isRefreshing?: boolean;
  /** When the last background refresh failed. The page keeps showing the
   *  data it has; the header says it could not refresh. */
  refreshFailedAt?: Date | null;
}

export const PourHeader: React.FC<PourHeaderProps> = ({
  pour,
  onRefresh,
  lastRefresh,
  isRefreshing,
  refreshFailedAt,
}) => {
  const { t } = useTranslation();
  // Times in the pour's own timezone, named ("14:05 AEDT").
  const tz = pour.timezone_iana;
  const startsText = pour.scheduled && pour.starts_at
    ? `${t('pourDashboard.starts', 'Starts')} ${formatDateTimeWithZone(pour.starts_at, tz)}`
    : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      {/* A pour that has not started yet: "Scheduled" with its start time
          instead of reading as under way. */}
      {pour.scheduled && (
        <Tooltip title={startsText ?? ''}>
          <Chip
            data-testid="pour-scheduled"
            size="small"
            icon={<EventIcon sx={{ fontSize: 16 }} />}
            label={t('pourDashboard.scheduled', 'Scheduled')}
            sx={{
              height: 24,
              fontWeight: 500,
              borderRadius: '999px',
              bgcolor: '#E6F1FB',
              color: '#185FA5',
              '& .MuiChip-icon': { color: '#185FA5' },
            }}
          />
        </Tooltip>
      )}
      {startsText && (
        <Typography
          variant="caption"
          noWrap
          sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}
        >
          {startsText}
        </Typography>
      )}

      {/* A failed background refresh keeps the last data on screen; this
          says so, and the tooltip says how old the data is. Phones show
          the icon only. */}
      {refreshFailedAt && (() => {
        const failedText = `${t('pourDashboard.couldNotRefresh', 'Could not refresh')}: ${formatTimeWithZone(refreshFailedAt, tz)}`;
        const dataText = lastRefresh
          ? `${t('pourDashboard.showingDataFrom', 'Showing data from')} ${formatTimeWithZone(lastRefresh, tz)}`
          : '';
        return (
          <Tooltip title={dataText ? `${failedText}. ${dataText}` : failedText}>
            <Box
              role="status"
              aria-label={failedText}
              data-testid="refresh-failed"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'warning.dark',
                fontSize: '0.75rem',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 16 }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {failedText}
              </Box>
            </Box>
          </Tooltip>
        );
      })()}

      {onRefresh && (
        <Tooltip
          title={
            lastRefresh
              ? `${t('pourDashboard.lastUpdated', 'Last updated')}: ${formatDateTimeWithZone(lastRefresh, tz)}`
              : t('pourDashboard.refreshData', 'Refresh data')
          }
        >
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={isRefreshing}
              size="small"
              aria-label={t('pourDashboard.refreshData', 'Refresh data')}
              sx={{
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
